// @ts-check
// Configurações > Privacidade (LGPD): pedidos de titulares. Busca a pessoa, exporta tudo em JSON ou CSV,
// anonimiza ou exclui com confirmação digitada e mostra se ela pode receber marketing em cada canal, com a base. Desde
// a 2.0.0 também a base legal das comunicações de marketing da escola (contrato 09 de campanhas base, seção 6.6).
import { html } from 'htm/preact';
import { useEffect, useState } from 'preact/hooks';
import { PageCard, PageHeader } from '../page/page-card.js';
import { SettingsSection } from '../settings/settings-pages.js';
import { hasCurrentPermissionFlag } from '../../data/metadata-store.js';
import { searchRecords } from '../../data/rest.js';
import { fetchMarketingStatus } from '../../data/marketing-consent.js';
import { anonymizeDataSubject, deleteDataSubject, exportDataSubject } from '../../data/operations.js';
import { Link } from '../../app/router.js';
import { Button } from '../../ui/button.js';
import { Icon } from '../../ui/icon.js';
import { Modal } from '../../ui/modal.js';
import { useDebouncedValue } from '../../ui/hooks.js';
import { showErrorSnackbar, showSuccessSnackbar } from '../../ui/snackbar.js';
import { downloadBlob } from '../../lib/csv.js';
import { showOperationsError } from './operations-format.js';
import { TrashRetentionSection } from './trash-retention-section.js';
import { MarketingLegalBasisSection } from '../privacy/marketing-legal-basis-section.js';
import { t } from '../../i18n/index.js';

const MARKETING_CHANNELS = ['EMAIL', 'WHATSAPP', 'SMS'];

/** @typedef {{ recordId: string, label: string }} PersonOption */

/** @param {{ onSelect: (person: PersonOption) => void }} props */
function PersonSearch({ onSelect }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(/** @type {PersonOption[] | null} */ (null));
  const debouncedQuery = useDebouncedValue(query, 250);
  useEffect(() => {
    const searchInput = debouncedQuery.trim();
    if (searchInput.length < 2) {
      setResults(null);
      return;
    }
    let isCancelled = false;
    searchRecords(searchInput, { limit: 8, includedObjectNameSingulars: ['person'] })
      .then((found) => !isCancelled && setResults(found.filter((item) => item.objectNameSingular === 'person')))
      .catch(() => !isCancelled && setResults([]));
    return () => {
      isCancelled = true;
    };
  }, [debouncedQuery]);
  return html`<div class="privacy-search">
    <label class="form-field">
      <span class="form-field-label">${t('operations.privacy.searchLabel')}</span>
      <input class="text-input" type="search" value=${query} placeholder=${t('operations.privacy.searchPlaceholder')}
        onInput=${(event) => setQuery(/** @type {HTMLInputElement} */ (event.currentTarget).value)} />
    </label>
    ${results !== null
      ? html`<div class="privacy-results" role="listbox">
          ${results.length === 0
            ? html`<span class="channel-card-description">${t('operations.privacy.noResults')}</span>`
            : results.map((result) => html`<button key=${result.recordId} type="button" role="option" class="privacy-result"
                onClick=${() => {
                  onSelect({ recordId: result.recordId, label: result.label });
                  setQuery('');
                  setResults(null);
                }}>
                <${Icon} name="IconUser" size=${14} /><span>${result.label}</span>
              </button>`)}
        </div>`
      : null}
  </div>`;
}

/** @param {{ person: PersonOption }} props */
function ConsentStatus({ person }) {
  // uma leitura da situação (2.0.0): canal, base de quem pode receber e motivo de quem não pode (inclusive OPTED_OUT)
  const [channels, setChannels] = useState(/** @type {Record<string, { isEligible: boolean, basis: string | null, reason: string | null }> | null} */ (null));
  useEffect(() => {
    setChannels(null);
    fetchMarketingStatus(person.recordId)
      .then((status) => setChannels(status.channels))
      .catch(showErrorSnackbar);
  }, [person.recordId]);
  return html`<ul class="privacy-consents" data-testid="privacy-consents">
    ${MARKETING_CHANNELS.map((channel) => {
      const decision = channels?.[channel];
      const isLoading = !decision;
      const text = isLoading ? ''
        : decision.isEligible
          ? (decision.basis ? t('operations.privacy.eligibleWithBasis', { basis: t(`operations.privacy.bases.${decision.basis}`) }) : t('operations.privacy.eligible'))
          : t(`operations.privacy.blockReasons.${decision.reason ?? 'MARKETING_BLOCKED'}`);
      return html`<li key=${channel} data-eligible=${!isLoading && decision.isEligible ? 'true' : 'false'}>
        <${Icon} name=${isLoading ? 'IconCircleDashed' : decision.isEligible ? 'IconCircleCheck' : 'IconForbid'} size=${14} />
        <span class="privacy-consent-channel">${t(`operations.privacy.channels.${channel}`)}</span>
        <span class="settings-table-muted">${text}</span>
      </li>`;
    })}
  </ul>`;
}

export function SettingsPrivacyPage() {
  const [person, setPerson] = useState(/** @type {PersonOption | null} */ (null));
  const [pendingAction, setPendingAction] = useState(/** @type {'anonymize' | 'delete' | null} */ (null));
  const [confirmation, setConfirmation] = useState('');
  const [isWorking, setIsWorking] = useState(false);
  // direitos do titular (LGPD) seguem a flag SECURITY do papel
  const isAdmin = hasCurrentPermissionFlag('SECURITY');
  const confirmWord = t('operations.privacy.confirmWord');

  useEffect(() => {
    document.title = `${t('operations.privacy.title')} - ${t('navigation.settings')}`;
  }, []);

  const download = async (/** @type {'json' | 'csv'} */ format) => {
    if (!person) return;
    try {
      const { blob, fileName } = await exportDataSubject(person.recordId, format);
      downloadBlob(blob, fileName);
      showSuccessSnackbar(t('operations.privacy.exported'));
    } catch (error) {
      showErrorSnackbar(error);
    }
  };

  const runPendingAction = async () => {
    if (!person || !pendingAction) return;
    setIsWorking(true);
    try {
      if (pendingAction === 'anonymize') {
        await anonymizeDataSubject(person.recordId);
        showSuccessSnackbar(t('operations.privacy.anonymized'));
        setPerson({ ...person, label: t('operations.privacy.anonymizedLabel') });
      } else {
        await deleteDataSubject(person.recordId);
        showSuccessSnackbar(t('operations.privacy.deleted'));
        setPerson(null);
      }
      setPendingAction(null);
      setConfirmation('');
    } catch (error) {
      showOperationsError(error);
    } finally {
      setIsWorking(false);
    }
  };

  const header = html`<${PageHeader} icon="IconShieldLock" title=${t('operations.privacy.title')} />`;
  if (!isAdmin) {
    return html`<${PageCard} header=${header}><div class="settings-page">
      <p class="channel-card-note"><${Icon} name="IconLock" size=${14} />${t('operations.privacy.adminOnly')}</p>
    </div><//>`;
  }
  return html`<${PageCard} header=${header}>
    <div class="settings-page">
      <${SettingsSection} title=${t('operations.privacy.title')} description=${t('operations.privacy.description')}>
        <${PersonSearch} onSelect=${setPerson} />
        ${person
          ? html`<div class="channel-card">
              <div class="channel-card-header">
                <${Icon} name="IconUser" size=${24} />
                <div class="channel-card-titles">
                  <span class="channel-card-title">${person.label}</span>
                  <span class="channel-card-description">${t('operations.privacy.selected')}</span>
                </div>
                <${Link} href=${`/object/person/${person.recordId}`} className="operations-link">${t('operations.privacy.openRecord')}<//>
              </div>
              <div class="privacy-action">
                <div class="channel-card-titles">
                  <span class="channel-card-title">${t('operations.privacy.exportTitle')}</span>
                  <span class="channel-card-description">${t('operations.privacy.exportDescription')}</span>
                </div>
                <div class="privacy-action-buttons">
                  <${Button} title=${t('operations.privacy.exportJson')} icon="IconJson" variant="secondary" size="small" onClick=${() => download('json')} />
                  <${Button} title=${t('operations.privacy.exportCsv')} icon="IconFileSpreadsheet" variant="secondary" size="small" onClick=${() => download('csv')} />
                </div>
              </div>
              <div class="privacy-action">
                <div class="channel-card-titles">
                  <span class="channel-card-title">${t('operations.privacy.consentTitle')}</span>
                  <span class="channel-card-description">${t('marketingConsent.privacyConsentDescription')}</span>
                </div>
              </div>
              <${ConsentStatus} person=${person} />
              <div class="privacy-action">
                <div class="channel-card-titles">
                  <span class="channel-card-title">${t('operations.privacy.anonymizeTitle')}</span>
                  <span class="channel-card-description">${t('operations.privacy.anonymizeDescription')}</span>
                </div>
                <div class="privacy-action-buttons">
                  <${Button} title=${t('operations.privacy.anonymize')} icon="IconUserX" variant="secondary" accent="danger" size="small" onClick=${() => setPendingAction('anonymize')} />
                </div>
              </div>
              <div class="privacy-action">
                <div class="channel-card-titles">
                  <span class="channel-card-title">${t('operations.privacy.deleteTitle')}</span>
                  <span class="channel-card-description">${t('operations.privacy.deleteDescription')}</span>
                </div>
                <div class="privacy-action-buttons">
                  <${Button} title=${t('operations.privacy.delete')} icon="IconTrash" variant="secondary" accent="danger" size="small" onClick=${() => setPendingAction('delete')} />
                </div>
              </div>
            </div>`
          : null}
      <//>
      <${MarketingLegalBasisSection} />
      <${TrashRetentionSection} />
    </div>
    ${pendingAction && person
      ? html`<${Modal}
          title=${t('operations.privacy.confirmTitle')}
          subtitle=${t(pendingAction === 'anonymize' ? 'operations.privacy.confirmAnonymize' : 'operations.privacy.confirmDelete', { word: confirmWord, name: person.label })}
          confirmLabel=${t(pendingAction === 'anonymize' ? 'operations.privacy.anonymize' : 'operations.privacy.delete')}
          confirmAccent="danger"
          isConfirmDisabled=${confirmation.trim().toUpperCase() !== confirmWord}
          isLoading=${isWorking}
          onClose=${() => {
            setPendingAction(null);
            setConfirmation('');
          }}
          onConfirm=${runPendingAction}
        >
          <input class="text-input" type="text" value=${confirmation} aria-label=${confirmWord} autofocus
            onInput=${(event) => setConfirmation(/** @type {HTMLInputElement} */ (event.currentTarget).value)} />
        <//>`
      : null}
  <//>`;
}
